// Left rail nav for the canvas surface. Three sections, structure
// locked from v6 RFC: My Account / Offerings / My Investments.
// Account + Offerings are static (mirror the metagraph); My
// Investments is dynamic (per the user's subscription list).

import { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  ACCOUNT_CELLS,
  INVESTMENT_CELLS,
  OFFERINGS,
  OFFERING_CELLS,
  type CellMeta,
} from '../../lib/cells';
import {
  fetchEnrichedSubscriptions,
  type EnrichedSubscription,
} from '../../lib/investor';
import { useTotalRaisedAcrossSales, formatUsdc } from '../../lib/onchain';

type Props = {
  investorId: string;
  /**
   * Phase 9.1 — when provided, sidebar items render as buttons that
   * dispatch the cell inline (chat-surface mode) rather than NavLinks
   * that route to /canvas (canvas-surface default). Lets the /chat
   * surface keep the user in-chat: clicking "KYC Status" appends a
   * cell to the chat stream, the same way "Browse offerings" chip does.
   */
  onCellClick?: (cell: CellMeta, ctx: { contractId?: string; investmentId?: string }) => void;
};

export function SidebarNav({ investorId, onCellClick }: Props) {
  const [subscriptions, setSubscriptions] = useState<EnrichedSubscription[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEnrichedSubscriptions(investorId)
      .then((subs) => {
        if (!cancelled) setSubscriptions(subs);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [investorId]);

  return (
    <nav className="space-y-6 text-sm">
      {/* Phase 7 §7.4 — live TVL banner. Reads totalRaised from the
          configured sale contract via viem. Shows "Loading…" until the
          first multicall resolves; quietly empty if no sale is deployed
          on the active chain (e.g. local Anvil without seed). */}
      <TvlBanner />

      <Section title="My Account">
        {ACCOUNT_CELLS.map((c) => (
          <CellLink key={c.name} cell={c} onCellClick={onCellClick} />
        ))}
      </Section>

      <Section title="Offerings">
        {OFFERINGS.map((o) => (
          <Group key={o.id} title={o.label} subtitle={o.subtitle}>
            {OFFERING_CELLS.map((c) => (
              <CellLink key={c.name} cell={c} contractId={o.id} onCellClick={onCellClick} />
            ))}
          </Group>
        ))}
      </Section>

      <Section title="My Investments">
        {error ? <ErrorLine>{error}</ErrorLine> : null}
        {subscriptions === null ? <Loading /> : null}
        {subscriptions && subscriptions.length === 0 ? (
          <p className="text-xs text-foreground-mute px-2 py-1">
            No active subscriptions yet. Browse offerings →
          </p>
        ) : null}
        {subscriptions?.map((sub) => (
          <InvestmentGroup key={sub.subscriptionId} sub={sub} onCellClick={onCellClick} />
        ))}
      </Section>
    </nav>
  );
}

// ─── TVL banner (Phase 7 §7.4) ───────────────────────────────────
//
// Aggregates totalRaised across all known sale contracts. Currently
// the only deployed sale is Sakura; useTotalRaisedAcrossSales reads
// from a static list and can be expanded when the restaurants/ list
// becomes dossier-driven. Hidden when no sale is deployed (e.g. a
// fresh local Anvil without seeded contracts) to avoid noise.

function TvlBanner() {
  const { data, loading, error } = useTotalRaisedAcrossSales();
  if (error || (!loading && data === null)) return null;
  return (
    <div className="rounded-md border border-muted-foreground/20 bg-muted/30 px-3 py-2 text-xs">
      <div className="text-[10px] uppercase tracking-wider text-foreground-mute">
        Total raised
      </div>
      <div className="font-mono">
        {loading || data === null ? 'Loading…' : formatUsdc(data)}
      </div>
    </div>
  );
}

// ─── Section + Group primitives ──────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-mute px-2 mb-2">
        {title}
      </h3>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Group({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md py-1">
      <div className="px-2 mb-1">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        {subtitle ? (
          <p className="text-[10px] text-foreground-mute truncate">{subtitle}</p>
        ) : null}
      </div>
      <div className="space-y-0.5 pl-2">{children}</div>
    </div>
  );
}

function InvestmentGroup({
  sub,
  onCellClick,
}: {
  sub: EnrichedSubscription;
  onCellClick?: (cell: CellMeta, ctx: { contractId?: string; investmentId?: string }) => void;
}) {
  const rmintLabel = prettyRmint(sub.subject?.rmintId ?? 'unknown');
  const phase = sub.state?.currentPhase ?? '—';
  return (
    <Group title={rmintLabel} subtitle={`${phase}`}>
      {INVESTMENT_CELLS.map((c) => (
        <CellLink
          key={c.name}
          cell={c}
          investmentId={sub.subscriptionId}
          onCellClick={onCellClick}
        />
      ))}
    </Group>
  );
}

function prettyRmint(rmintId: string): string {
  return rmintId
    .replace(/^rest_/, '')
    .split(/[-_]/)
    .map((s) => (s ? s[0]!.toUpperCase() + s.slice(1) : ''))
    .join(' ');
}

// ─── CellLink ────────────────────────────────────────────────────

function CellLink({
  cell,
  contractId,
  investmentId,
  onCellClick,
}: {
  cell: CellMeta;
  contractId?: string;
  investmentId?: string;
  onCellClick?: (cell: CellMeta, ctx: { contractId?: string; investmentId?: string }) => void;
}) {
  const [params] = useSearchParams();
  const isActive =
    params.get('cell') === cell.name &&
    (params.get('contract') ?? undefined) === contractId &&
    (params.get('investment') ?? undefined) === investmentId;

  // Phase 9.1 — chat-surface mode: render a button that dispatches the
  // cell inline rather than navigating. Canvas surface (no onCellClick)
  // keeps the original NavLink → /canvas?cell=X behavior.
  if (onCellClick) {
    return (
      <button
        type="button"
        onClick={() => onCellClick(cell, { contractId, investmentId })}
        className="block w-full text-left px-2 py-1 rounded text-xs transition-colors text-foreground-mute hover:bg-muted hover:text-foreground cursor-pointer"
      >
        {cell.title}
      </button>
    );
  }

  const target = buildTarget(cell.name, { contractId, investmentId });
  return (
    <NavLink
      to={target}
      end
      className={() =>
        `block px-2 py-1 rounded text-xs transition-colors ${
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-foreground-mute hover:bg-muted hover:text-foreground'
        }`
      }
    >
      {cell.title}
    </NavLink>
  );
}

function buildTarget(
  cellName: string,
  { contractId, investmentId }: { contractId?: string; investmentId?: string },
): string {
  const params = new URLSearchParams();
  params.set('cell', cellName);
  if (contractId) params.set('contract', contractId);
  if (investmentId) params.set('investment', investmentId);
  return `/canvas?${params.toString()}`;
}

function Loading() {
  return (
    <div className="space-y-1 px-2">
      {[0, 1].map((i) => (
        <div key={i} className="h-3 rounded bg-muted/60 animate-pulse" />
      ))}
    </div>
  );
}

function ErrorLine({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-destructive px-2">{children}</p>;
}
